import React, { useState } from 'react';
import { Brain } from 'lucide-react';
import { deleteMemoryNote } from '@/lib/writer/memoryClient';
import type { WriterMemoryNoteRef } from '@/hooks/useWriterThread';

interface Props {
  notes: WriterMemoryNoteRef[];
}

/**
 * The quiet line under a reply that shows what Writer just wrote to memory.
 *
 * This is where the feature is discovered: the author sees a note being
 * taken in their own words, and can undo it on the spot.
 */
const WriterMemoryNoteLine: React.FC<Props> = ({ notes }) => {
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const visible = notes.filter((n) => !removed.has(n.id));
  if (visible.length === 0) return null;

  const undo = async (id: string) => {
    setRemoved((prev) => new Set(prev).add(id));
    try {
      await deleteMemoryNote(id);
    } catch {
      setRemoved((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="mt-1.5 max-w-[92%] w-full space-y-1">
      {visible.map((note) => (
        <p
          key={note.id}
          className="flex items-start gap-1.5 px-1 text-xs text-muted-foreground"
        >
          <Brain className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            Noted: {note.note}
          </span>
          <button
            type="button"
            onClick={() => void undo(note.id)}
            className="shrink-0 underline underline-offset-2 hover:text-foreground transition"
          >
            Undo
          </button>
        </p>
      ))}
    </div>
  );
};

export default WriterMemoryNoteLine;
